import axios from "../AxiosInterCeptor/AxiosInterCeptor";

/**
 * Brisk certificates (new, "V2") — every call of the /briskCreateCertificate page and the Brisk Certificate List, to
 * the backend's /briskV2 routes (the backend talks to Brisk; this file never calls Brisk itself).
 *
 * Imported directly by the new pages (Service/_index.js is not touched). Never throws: the result is always the
 * server's envelope { error, message, data, code? }, or { error: true, message } when the server answered with an
 * HTTP error, or { error: true, noResponse: true, message } when nothing answered (then the same requestId may be
 * sent again — AddMoneyToUser/addMoneyUtils.js shouldReuseRequestId).
 *
 * Creating a certificate has NO timeout here: Brisk may take a while, and the server decides what an unanswered
 * create means (it never charges twice for one requestId).
 */
const URL = process.env.React_App_BaseUrl;
const BASE = () => `${URL}/briskV2`;

const NO_RESPONSE = "No answer from the server. Please check your connection and try again";

const settle = (request) =>
  request
    .then((response) => {
      const body = response && response.data;
      return body && typeof body === "object" ? body : { error: true, message: "Unexpected answer from the server" };
    })
    .catch((error) => {
      if (error && error.response) {
        const body = error.response.data || {};
        return { error: true, message: body.message || `The server answered ${error.response.status}`, code: body.code };
      }
      return { error: true, noResponse: true, message: NO_RESPONSE };
    });

export const getBriskV2Config = () => settle(axios.get(`${BASE()}/config`));
export const getBriskV2Lists = () => settle(axios.get(`${BASE()}/lists`));
export const getBriskV2Models = (make) => settle(axios.get(`${BASE()}/models`, { params: { make } }));
export const getBriskV2Balance = () => settle(axios.get(`${BASE()}/balance`));
/** Brisk's own (dealer) balance — only for the logins the backend's BRISK_V2_DEALER_BALANCE_FOR allows. */
export const getBriskV2DealerBalance = () => settle(axios.get(`${BASE()}/dealerBalance`));

/** One Online Policy's certificates made through the new API (page or automation), with the saved Brisk calls. */
export const getBriskV2PolicyCertificates = (policyId) =>
  settle(axios.get(`${BASE()}/policyCertificates/${encodeURIComponent(policyId)}`));

export const getBriskV2PendingPolicies = () => settle(axios.get(`${BASE()}/pendingPolicies`));
export const getBriskV2PendingPolicy = (policyId) =>
  settle(axios.get(`${BASE()}/pendingPolicies/${encodeURIComponent(policyId)}`));

export const saveBriskV2Draft = ({ draftId, ...body }) =>
  settle(draftId ? axios.put(`${BASE()}/drafts/${encodeURIComponent(draftId)}`, body) : axios.post(`${BASE()}/drafts`, body));

export const createBriskV2Certificate = (body) => settle(axios.post(`${BASE()}/certificates`, body));
export const listBriskV2Certificates = (params) => settle(axios.get(`${BASE()}/certificates`, { params }));
export const getBriskV2Certificate = (id) => settle(axios.get(`${BASE()}/certificates/${encodeURIComponent(id)}`));
// { fileName }: the link saves the file under that name. Without it the link shows the file in the tab. Both are our S3 copy.
export const getBriskV2DownloadUrl = (id, params) => settle(axios.get(`${BASE()}/certificates/${encodeURIComponent(id)}/download`, { params }));
export const getBriskV2Details = (id) => settle(axios.get(`${BASE()}/certificates/${encodeURIComponent(id)}/details`));
export const updateBriskV2Certificate = (id, body) =>
  settle(axios.put(`${BASE()}/certificates/${encodeURIComponent(id)}`, body));
export const cancelBriskV2Certificate = (id, reason) =>
  settle(axios.post(`${BASE()}/certificates/${encodeURIComponent(id)}/cancel`, { reason }));
export const recheckBriskV2Certificate = (id) =>
  settle(axios.post(`${BASE()}/certificates/${encodeURIComponent(id)}/recheck`, {}));
export const resolveBriskV2Certificate = (id, body) =>
  settle(axios.post(`${BASE()}/certificates/${encodeURIComponent(id)}/resolve`, body));

export const listBriskV2Topups = () => settle(axios.get(`${BASE()}/topups`));
export const startBriskV2Topup = (body) => settle(axios.post(`${BASE()}/topups`, body));
export const checkBriskV2Topup = (id) => settle(axios.post(`${BASE()}/topups/${encodeURIComponent(id)}/check`, {}));
export const listBriskV2PaymentLinks = () => settle(axios.get(`${BASE()}/paymentLinks`));
export const sendBriskV2PaymentLink = (body) => settle(axios.post(`${BASE()}/paymentLinks`, body));

// Brisk money pages (admin): reads of the Company Wallet "Brisk". `params` = page, limit, search, type / status,
// startDate, endDate (YYYY-MM-DD), clientId (super admin), export ("1" = every row, up to 5000).
export const getBriskV2WalletSummary = () => settle(axios.get(`${BASE()}/wallet/summary`));
export const getBriskV2WalletLedger = (params) => settle(axios.get(`${BASE()}/wallet/ledger`, { params }));
export const getBriskV2WalletTopups = (params) => settle(axios.get(`${BASE()}/wallet/topups`, { params }));
/** A top-up receipt's short-lived link (the Company Wallet's own receipt endpoint). */
export const getBriskV2ReceiptUrl = (key) => settle(axios.get(`${URL}/getReceiptImage`, { params: { key } }));

// Bulk Upload: the Excel file in `formData` ("file"); commit also sends "requestId". Saves drafts only.
export const verifyBriskV2Bulk = (formData) => settle(axios.post(`${BASE()}/bulk/verify`, formData));
export const commitBriskV2Bulk = (formData) => settle(axios.post(`${BASE()}/bulk/commit`, formData));
